
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import NavBar from "@/components/NavBar";
import { useProject } from "@/contexts/ProjectContext";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";

export default function NewRisk() {
  const navigate = useNavigate();
  const { currentProject, addRisk } = useProject();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    stage: currentProject?.currentStage.toString() || "0",
    probability: "1",
    impact: "1",
    owner: "",
    mitigation: "",
  });

  if (!currentProject) {
    return <div>Loading...</div>;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.owner || !formData.mitigation) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    const newRisk = {
      id: `risk-${Date.now().toString(36)}`,
      projectId: currentProject.id,
      name: formData.name,
      description: formData.description,
      stage: parseInt(formData.stage),
      probability: parseInt(formData.probability),
      impact: parseInt(formData.impact),
      owner: formData.owner,
      mitigation: formData.mitigation,
      status: "open" as const,
      dateIdentified: new Date(),
      dateUpdated: new Date(),
    };

    addRisk(currentProject.id, newRisk);
    
    toast({
      title: "Risk added",
      description: "The risk has been added to the register",
    });
    
    navigate("/risks");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="container py-6 flex-1">
        <Button 
          variant="outline" 
          onClick={() => navigate("/risks")} 
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Risks
        </Button>
        
        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle>Add New Risk</CardTitle>
            <CardDescription>Create a new project risk entry</CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Risk Name</Label>
                <Input 
                  id="name" 
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Brief title describing the risk"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Provide details about this risk"
                  required
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="stage">RIBA Stage</Label>
                  <Select 
                    value={formData.stage}
                    onValueChange={(value) => handleSelectChange("stage", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {currentProject.stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id.toString()}>
                          Stage {stage.id}: {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="owner">Risk Owner</Label>
                  <Input 
                    id="owner" 
                    name="owner"
                    value={formData.owner}
                    onChange={handleChange}
                    placeholder="Person responsible for this risk"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="probability">Probability (1-5)</Label>
                  <Select 
                    value={formData.probability}
                    onValueChange={(value) => handleSelectChange("probability", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select probability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Very Low</SelectItem>
                      <SelectItem value="2">2 - Low</SelectItem>
                      <SelectItem value="3">3 - Medium</SelectItem>
                      <SelectItem value="4">4 - High</SelectItem>
                      <SelectItem value="5">5 - Very High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="impact">Impact (1-5)</Label>
                  <Select 
                    value={formData.impact}
                    onValueChange={(value) => handleSelectChange("impact", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select impact" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Minimal</SelectItem>
                      <SelectItem value="2">2 - Minor</SelectItem>
                      <SelectItem value="3">3 - Moderate</SelectItem>
                      <SelectItem value="4">4 - Major</SelectItem>
                      <SelectItem value="5">5 - Severe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="mitigation">Mitigation Strategy</Label>
                <Textarea
                  id="mitigation"
                  name="mitigation"
                  value={formData.mitigation}
                  onChange={handleChange}
                  placeholder="How will this risk be addressed or mitigated?"
                  required
                  rows={3}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => navigate("/risks")}>
                Cancel
              </Button>
              <Button type="submit">Add Risk</Button>
            </CardFooter>
          </form>
        </Card>
      </main>
    </div>
  );
}
